import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getMediumByKey } from 'utils/messageMediums';
import { useChannels, useEspAccounts, useMessageTypes, usePagination } from '../hooks';
import { ChannelsContext } from './ChannelsContext';
import { useSearchContext } from './SearchContext';

export const ChannelsContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { messageMedium } = useParams<{ messageMedium: string }>();
  const dbKey = getMediumByKey(messageMedium)?.messageType || '';
  const { debouncedValue } = useSearchContext();
  const { currentPage, itemsPerPage, handlePaginationUpdate } = usePagination();
  const { espAccounts, getEspAccountById, isLoadingEspAccounts } = useEspAccounts(messageMedium);
  const {
    isLoadingMessageTypes,
    messageTypes = [],
    messageTypesByChannelId,
    refetchMessageTypes,
  } = useMessageTypes();
  const {
    currentChannels,
    filteredChannels,
    isLoadingChannels,
    paginatedChannels,
    refetchChannels,
  } = useChannels(dbKey, debouncedValue, currentPage, itemsPerPage, messageTypesByChannelId);

  useEffect(() => {
    setIsLoading(isLoadingChannels || isLoadingEspAccounts || isLoadingMessageTypes);
  }, [isLoadingChannels, isLoadingEspAccounts, isLoadingMessageTypes]);

  // Set to first page when filtered results or messageMedium changes
  useEffect(() => {
    handlePaginationUpdate({ pageSize: itemsPerPage, page: 1 });
  }, [filteredChannels, messageMedium]);

  return (
    <ChannelsContext.Provider
      value={{
        filteredChannels,
        currentChannels,
        currentPage,
        espAccounts,
        getEspAccountById, // convert to utility function and remove from useEspAccounts
        handlePaginationUpdate,
        isLoading,
        itemsPerPage,
        messageMedium,
        messageTypes, // shouldn't really need this
        messageTypesByChannelId,
        paginatedChannels,
        refetchChannels,
        refetchMessageTypes,
      }}
    >
      {children}
    </ChannelsContext.Provider>
  );
};
